import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { num } from '../lib/format.js';
import { pendingMaterial, sendOrQueue } from '../lib/outbox.js';
import { useToast } from '../state/toast.jsx';
import { useOutbox } from '../state/outbox.jsx';

const FLUSH_MS = 700;

/**
 * Material buchen mit minimaler Latenz:
 *  - jeder Tipp ist sofort sichtbar (optimistisch),
 *  - schnelle Tipps auf denselben Artikel werden gebündelt (ein Exxas-Call statt fünf),
 *  - pro Artikel strikt nacheinander, damit keine Mengen verloren gehen,
 *  - Toast mit «Rückgängig»,
 *  - ohne Netz in die Outbox (Anzeige rechnet vorgemerkte Mengen mit ein).
 */
export function useMaterialBooking(nr) {
  const qc = useQueryClient();
  const toast = useToast();
  const pending = useRef(new Map()); // artikelId → { delta, artikel, timer }
  const chains = useRef(new Map()); // artikelId → Promise
  const [, force] = useState(0);
  useOutbox(); // neu rendern, wenn sich vorgemerkte Buchungen ändern
  const queuedHint = useRef(0); // «Vorgemerkt» höchstens einmal pro Minute melden, sonst verdrängt er «Rückgängig»
  const rerender = () => force((n) => n + 1);
  const key = ['material', nr];

  const applyServer = useCallback((artikel, res) => {
    qc.setQueryData(key, (list = []) => {
      const rest = list.filter((p) => p.artikelId !== artikel.id);
      if (res.geloescht || res.menge <= 0) return rest;
      const old = list.find((p) => p.artikelId === artikel.id);
      return [...rest, { ...(old ?? { titel: artikel.titel, bezeichnung: artikel.nummer, einheit: artikel.einheit }), id: res.positionId, artikelId: artikel.id, menge: res.menge }];
    });
  }, [qc, nr]); // eslint-disable-line react-hooks/exhaustive-deps

  const flush = useCallback((artikelId) => {
    const p = pending.current.get(artikelId);
    if (!p || p.delta === 0) {
      if (p && !p.inflight) pending.current.delete(artikelId);
      rerender();
      return;
    }
    const { delta, artikel } = p;
    p.delta = 0;
    const prev = chains.current.get(artikelId) ?? Promise.resolve();
    const next = prev.then(async () => {
      try {
        const r = await sendOrQueue({
          kind: 'material',
          nummer: nr,
          uid: qc.getQueryData(['me'])?.uid ?? null,
          label: `${delta > 0 ? '+' : '−'}${num(Math.abs(delta))} ${artikel.einheit} ${artikel.titel}`,
          path: `/auftraege/${encodeURIComponent(nr)}/material`,
          body: { artikelId: artikel.id, delta },
        });
        if (!r.queued) applyServer(artikel, r.res);
        else if (Date.now() - queuedHint.current > 60_000) {
          queuedHint.current = Date.now();
          toast({ text: 'Kein Netz: Material ist vorgemerkt und wird automatisch gebucht', long: true });
        }
      } catch (err) {
        toast({ text: `${artikel.titel}: nicht gebucht – ${err.message}`, error: true });
        qc.invalidateQueries({ queryKey: key });
      } finally {
        const cur = pending.current.get(artikelId);
        if (cur) cur.inflight = (cur.inflight ?? 0) - delta;
        if (cur && cur.delta === 0 && !cur.inflight) pending.current.delete(artikelId);
        rerender();
      }
    });
    chains.current.set(artikelId, next);
    const cur = pending.current.get(artikelId);
    cur.inflight = (cur.inflight ?? 0) + delta;
  }, [nr, applyServer, toast, qc]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Menge ändern (positiv oder negativ). */
  const book = useCallback((artikel, delta, { silent = false } = {}) => {
    const cur = pending.current.get(artikel.id) ?? { delta: 0, inflight: 0, artikel };
    cur.delta += delta;
    cur.artikel = artikel;
    clearTimeout(cur.timer);
    cur.timer = setTimeout(() => flush(artikel.id), FLUSH_MS);
    pending.current.set(artikel.id, cur);
    rerender();
    if (!silent) {
      const total = (qc.getQueryData(key) ?? []).find((p) => p.artikelId === artikel.id)?.menge ?? 0;
      const shown = total + cur.delta + (cur.inflight ?? 0) + pendingMaterial(nr, artikel.id);
      toast({
        text: `${delta > 0 ? '+' : '−'}${num(Math.abs(delta))} ${artikel.einheit} ${artikel.titel} · total ${num(Math.max(0, shown))}`,
        undo: () => book(artikel, -delta, { silent: true }),
      });
    }
  }, [flush, qc, toast, nr]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Angezeigte Menge = Server + noch nicht bestätigte Änderungen. */
  const qtyOf = useCallback((artikelId, serverMenge = 0) => {
    const p = pending.current.get(artikelId);
    return Math.max(0, serverMenge + (p ? p.delta + (p.inflight ?? 0) : 0) + pendingMaterial(nr, artikelId));
  }, [nr]);

  /** Freitext-Material (nicht in den Stammdaten). */
  const bookFreitext = useCallback(async (titel, menge = 1) => {
    try {
      const r = await sendOrQueue({
        kind: 'material',
        nummer: nr,
        uid: qc.getQueryData(['me'])?.uid ?? null,
        label: `${num(menge)} × «${titel}» (Freitext)`,
        path: `/auftraege/${encodeURIComponent(nr)}/material`,
        body: { freitext: true, titel, delta: menge },
      });
      qc.invalidateQueries({ queryKey: key });
      toast({ text: r.queued ? `Vorgemerkt: «${titel}» wird gebucht, sobald Netz da ist` : `${num(menge)} × «${titel}» als Freitext gebucht` });
    } catch (err) {
      toast({ text: `Nicht gebucht: ${err.message}`, error: true });
    }
  }, [nr, qc, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  // Beim Verlassen ausstehende Buchungen sofort senden
  useEffect(() => () => {
    for (const [id, p] of pending.current) { clearTimeout(p.timer); if (p.delta) flush(id); }
  }, [flush]);

  return { book, qtyOf, bookFreitext };
}
